import React, { createContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

// Création du contexte Auth
export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  /**
   * Extrait les données utilisateur du token décodé
   * Supporte les deux types: admin et coach
   */
  const extractUserData = useCallback((decoded) => {
    return {
      id: decoded.id || decoded._id,
      email: decoded.email,
      firstName: decoded.firstName,
      lastName: decoded.lastName,
      role: decoded.role, // 'admin' ou 'coach'

      // Données tenant (coach uniquement)
      tenantId: decoded.tenantId || null,
      tenantDbName: decoded.tenantDbName || null,
      tenantStatus: decoded.tenantStatus || null,

      // Données abonnement (coach uniquement)
      subscriptionPlan: decoded.subscriptionPlan || null,
      maxClients: decoded.maxClients || null,
      isSubscribed: decoded.isSubscribed || false,
      stripeCustomerId: decoded.stripeCustomerId || null,

      // Expiration
      exp: decoded.exp,
    };
  }, []);

  /**
   * Connecte l'utilisateur avec le token JWT
   */
  const login = useCallback((token) => {
    localStorage.setItem('authToken', token);
    const decoded = jwtDecode(token);
    const userData = extractUserData(decoded);

    setAuthToken(token);
    setUser(userData);
    setLoading(false);
  }, [extractUserData]);

  /**
   * Déconnecte l'utilisateur
   */
  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    setAuthToken(null);
    setUser(null);
    setLoading(false);

    Promise.resolve().then(() => {
      navigate('/');
    });
  }, [navigate]);

  /**
   * Charge les informations d'authentification depuis localStorage au démarrage
   */
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 > Date.now()) {
          const userData = extractUserData(decoded);
          setAuthToken(token);
          setUser(userData);
        } else {
          logout();
        }
      } catch (error) {
        console.error('Erreur lors du décodage du token:', error);
        logout();
      }
    }
    setLoading(false);
  }, [logout, extractUserData]);

  /**
   * Rafraîchit le token (optionnel, si supporté par le backend)
   */
  const refreshAuthToken = useCallback(async () => {
    if (!authToken || !user) return;

    try {
      const endpoint = (user.role === 'admin' || user.role === 'super_admin')
        ? '/admin/auth/refresh-token'
        : '/coach/auth/refresh-token';

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      login(data.token);
    } catch (error) {
      console.error('Erreur lors du rafraîchissement du token:', error);
      // Ne pas déconnecter automatiquement en cas d'échec de refresh
    }
  }, [authToken, user, login]);

  /**
   * Rafraîchissement périodique du token (15 minutes)
   */
  useEffect(() => {
    let interval;
    if (authToken) {
      interval = setInterval(() => {
        refreshAuthToken();
      }, 15 * 60 * 1000);
    }
    return () => clearInterval(interval);
  }, [authToken, refreshAuthToken]);

  /**
   * Met à jour le profil utilisateur
   */
  const updateProfile = async (updatedUserInfo) => {
    if (!user) return;

    try {
      let data;
      if (user.role === 'admin') {
        // Admin profile update (à implémenter si nécessaire)
        data = await authService.getAdminProfile();
      } else {
        data = await authService.updateCoachProfile(updatedUserInfo);
      }

      // Mettre à jour les données locales
      setUser(prev => ({
        ...prev,
        ...data,
      }));

      return data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      throw error;
    }
  };

  /**
   * Vérifie si l'utilisateur a un rôle admin (admin ou super_admin)
   */
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  /**
   * Vérifie si l'utilisateur est coach
   */
  const isCoach = user?.role === 'coach';

  /**
   * Vérifie si le coach est abonné
   */
  const isSubscribed = user?.isSubscribed === true;

  /**
   * Vérifie si le tenant est actif
   */
  const isTenantActive = user?.tenantStatus === 'active';

  /**
   * Détermine la route de redirection après login
   */
  const getRedirectPath = useCallback(() => {
    if (!user) return '/';

    if (user.role === 'admin' || user.role === 'super_admin') {
      return '/admin';
    }

    if (user.role === 'coach') {
      return '/coach';
    }

    return '/';
  }, [user]);

  return (
    <AuthContext.Provider value={{
      // Données
      user,
      authToken,
      loading,

      // Rôles
      role: user?.role,
      isAdmin,
      isCoach,
      isSubscribed,
      isTenantActive,

      // Données tenant (coach)
      tenantId: user?.tenantId,
      subscriptionPlan: user?.subscriptionPlan,
      maxClients: user?.maxClients,

      // Actions
      login,
      logout,
      refreshAuthToken,
      updateProfile,
      getRedirectPath,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
